import React, { useEffect, useState } from "react";
import { Button, Modal } from "../../components/ui";

const googleViewerUrl = (fileUrl) =>
  `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`;

const officeViewerUrl = (fileUrl) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;

const FileViewerModal = ({ isOpen, onClose, file = {}, kind = "OTHER" }) => {
  const [excelRows, setExcelRows] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [pdfMode, setPdfMode] = useState("google");
  const [officeError, setOfficeError] = useState(false);

  const url = file?.url || "";
  const name = file?.originalName || "file";

  useEffect(() => {
    if (!isOpen) {
      setExcelRows(null);
      setExcelLoading(false);
      setPdfMode("google");
      setOfficeError(false);
      return;
    }

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || kind !== "EXCEL" || !url) return;

    let cancelled = false;
    (async () => {
      setExcelLoading(true);
      setExcelRows(null);
      try {
        const XLSX = await import("xlsx");
        const res = await fetch(url);
        if (!res.ok) throw new Error("Failed to fetch spreadsheet");
        const buffer = await res.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
        if (!cancelled) setExcelRows(data);
      } catch (err) {
        if (!cancelled) {
          setExcelRows(null);
        }
      } finally {
        if (!cancelled) setExcelLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, kind, url]);

  if (!isOpen || !url) return null;

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const renderPdf = () => {
    const src =
      pdfMode === "google" ? googleViewerUrl(url) : `${url}#toolbar=1&navpanes=0`;

    return (
      <div className="flex flex-col gap-2 h-[75vh]">
        <iframe
          title={name}
          src={src}
          className="w-full flex-1 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 shadow-xs"
        />
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => setPdfMode((m) => (m === "google" ? "direct" : "google"))}
          >
            Try {pdfMode === "google" ? "direct" : "Google"} viewer
          </Button>
          <span className="text-neutral-500 dark:text-neutral-400">
            If preview is blank, use Download or Open tab.
          </span>
        </div>
      </div>
    );
  };

  const renderOfficeEmbed = (label) => (
    <div className="flex flex-col gap-2 h-[75vh]">
      {!officeError ? (
        <iframe
          title={name}
          src={officeViewerUrl(url)}
          className="w-full flex-1 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 shadow-xs"
          onError={() => setOfficeError(true)}
        />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800">
          <p className="text-xs text-neutral-700 dark:text-neutral-200">
            Could not embed {label} preview. The file may be private or blocked for external viewers.
          </p>
          <div className="flex gap-2">
            <Button variant="primary" size="sm" onClick={handleDownload}>
              Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              tag="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in new tab
            </Button>
          </div>
        </div>
      )}
      <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
        In-app preview uses Microsoft Office Online (file URL must be publicly reachable).
      </p>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={name}
      size="xl"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 pb-3">
          <p className="text-xs font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">{kind}</p>
          <div className="flex items-center gap-2">
            <Button variant="primary" size="xs" onClick={handleDownload}>
              Download
            </Button>
            <Button
              variant="outline"
              size="xs"
              tag="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open tab
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-neutral-50 dark:bg-neutral-950 p-2 rounded-xl border border-neutral-200/60 min-h-[300px]">
          {kind === "IMAGE" && (
            <div className="flex items-center justify-center h-[70vh]">
              <img
                src={url}
                alt={name}
                className="max-w-full max-h-full object-contain rounded-lg shadow-sm"
              />
            </div>
          )}

          {kind === "PDF" && renderPdf()}

          {kind === "EXCEL" && (
            <div className="space-y-3">
              {excelLoading && (
                <p className="p-4 text-xs text-neutral-500 dark:text-neutral-400 text-center">Loading spreadsheet…</p>
              )}
              {excelRows && excelRows.length > 0 ? (
                <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-auto max-h-[75vh]">
                  <table className="min-w-full text-xs border-collapse">
                    <tbody>
                      {excelRows.slice(0, 200).map((row, ri) => (
                        <tr
                          key={ri}
                          className={
                            ri === 0
                              ? "bg-neutral-100/80 font-semibold text-neutral-900 dark:text-neutral-100"
                              : "border-t border-neutral-100 dark:border-neutral-800 text-neutral-700 dark:text-neutral-200"
                          }
                        >
                          {(Array.isArray(row) ? row : [row]).map((cell, ci) => (
                            <td
                              key={ci}
                              className="px-3 py-2 border-r border-neutral-100 dark:border-neutral-800 whitespace-nowrap max-w-[200px] truncate"
                            >
                              {cell == null ? "" : String(cell)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                !excelLoading && renderOfficeEmbed("Excel")
              )}
            </div>
          )}

          {kind === "WORD" && renderOfficeEmbed("Word")}

          {kind === "OTHER" && (
            <div className="flex flex-col items-center justify-center h-[50vh] gap-3 p-8 text-center">
              <span className="text-4xl">📎</span>
              <p className="text-xs text-neutral-800 dark:text-neutral-100 font-semibold">{name}</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 max-w-xs">
                Preview not available for this file type. Download or open in a new tab.
              </p>
              <div className="flex gap-2 mt-2">
                <Button variant="primary" size="sm" onClick={handleDownload}>
                  Download
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  tag="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in new tab
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FileViewerModal;